



import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  TextField,
  Autocomplete,
  CircularProgress,
  TableSortLabel,
  Pagination,
  Box,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Header from '../../Header';
import { CSVLink } from 'react-csv';
import { local_signage, local_url } from '../../Const';

const api_base_url = `${local_signage}`;
const ROWS_PER_PAGE = 200;

export const RateTrends = () => {
  // State for filter values
  const [branchType, setBranchType] = useState('');
  const [area, setArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchInputValue, setBranchInputValue] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  const [searchQuery, setSearchQuery] = useState('');

  // State for dropdown options
  const [segments, setSegments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [branchList, setBranchList] = useState([]);

  // State for data and loading
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState([]);

  // Fetch segments on component mount
  useEffect(() => {
    fetchSegments();
  }, []);

  // Set default dates if segment is Retail
  useEffect(() => {
    if (isRetail) {
      const today = dayjs();
      setFromDate(today.subtract(2, 'day'));
      setToDate(today);
    }
  }, [branchType]);

  // Fetch areas when segment changes
  useEffect(() => {
    if (branchType) {
      fetchAreas(branchType);
      setArea('');
      setSelectedBranch(null);
      setBranchList([]);
    }
  }, [branchType]);

  // Fetch branches when area changes
  useEffect(() => {
    if (branchType && area) {
      fetchBranches(area, branchType);
      setSelectedBranch(null);
    }
  }, [area, branchType]);

  // Update filtered data when search query or report data changes
  useEffect(() => {
    if (reportData.length > 0) {
      filterData();
    }
  }, [searchQuery, reportData]);

  // Update paginated data when filtered data or page changes
  useEffect(() => {
    updatePaginatedData();
  }, [filteredData, page]);

  // Function to update paginated data
  const updatePaginatedData = () => {
    const startIndex = (page - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  };

  // Function to fetch business segments
  const fetchSegments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/BizSegments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch segments: ${response.status}`);
      }
      
      const data = await response.json();
      setSegments(data);
    } catch (error) {
      console.error("Error fetching segments:", error);
      setError("Failed to load business segments. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch areas by segment
  const fetchAreas = async (segmentCode) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${segmentCode}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch areas: ${response.status}`);
      }
      
      const data = await response.json();
      setAreas(data);
    } catch (error) {
      console.error("Error fetching areas:", error);
      setError("Failed to load areas. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch branches by area and segment
  const fetchBranches = async (areaId, segmentCode) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('AuthorizationToken');
      const areaParam = areaId === 'ALL' ? 'ALL' : areaId;
      const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${segmentCode}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch branches: ${response.status}`);
      }
      
      const data = await response.json();
      setBranchList(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setError("Failed to load branches. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Updated function to fetch report data using the new API endpoint
  const fetchReportData = async () => {
    if (!branchType || !fromDate || !toDate) {
      alert('Please select required filters');
      return;
    }

    if (!selectedBranch) {
      alert('Please select a Branch');
      return;
    }

    // Check date range restriction
    const daysDifference = toDate.diff(fromDate, 'day');
    if (isRetail && daysDifference > 2) {
      alert('For Retail segment, date range cannot exceed 2 days.');
      return;
    }
    if (!isRetail && daysDifference > 7) {
      alert('For this segment, date range cannot exceed 7 days.');
      return;
    }

    if (daysDifference < 0) {
      alert('To Date cannot be earlier than From Date.');
      return;
    }

    // Check if dates are not in the future
    const today = dayjs();
    if (fromDate.isAfter(today, 'day')) {
      alert('From Date cannot be in the future.');
      return;
    }

    if (toDate.isAfter(today, 'day')) {
      alert('To Date cannot be in the future.');
      return;
    }

    try {
      setLoading(true);
      setFilteredData([]);
      setReportData([]);
      setPaginatedData([]);
      setPage(1);
      const token = localStorage.getItem('AuthorizationToken');
      
      // Prepare parameters for API call
      const segmentParam = branchType || 'ALL';
      const areaParam = area || 'ALL';
      const branchParam = selectedBranch?.branchID || 'ALL';
      const fromDateParam = fromDate.format('YYYYMMDD');
      const toDateParam = toDate.format('YYYYMMDD');
      
      // Construct the URL with query parameters
      const url = `${api_base_url}/api/v1/branchratetrend?Segment=${segmentParam}&AreaId=${areaParam}&BranchId=${branchParam}&FromDate=${fromDateParam}&ToDate=${toDateParam}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.status}`);
      }
      
      const data = await response.json();
      setReportData(data);
      setFilteredData(data);
      
      // Apply initial sorting if orderBy is set
      if (orderBy) {
        handleSort(orderBy);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError("Failed to load report data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle sort request
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    const sortedData = [...filteredData].sort((a, b) => {
      // Handle different property types
      let aValue, bValue;
      
      switch(property) {
        case 'BranchName':
          aValue = a.BranchName || '';
          bValue = b.BranchName || '';
          return newOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        
        case 'PartNumber':
          aValue = a.PartNumber || '';
          bValue = b.PartNumber || '';
          return newOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        
        case 'ItemName':
          aValue = a.ItemName || '';
          bValue = b.ItemName || '';
          return newOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        
        case 'BusinessDate':
          aValue = a.BusinessDate ? new Date(a.BusinessDate) : new Date(0);
          bValue = b.BusinessDate ? new Date(b.BusinessDate) : new Date(0);
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        case 'RateType':
          aValue = a.RateType || '';
          bValue = b.RateType || '';
          return newOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        
        case 'RetailPrice':
          aValue = a.RetailPrice || 0;
          bValue = b.RetailPrice || 0;
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        case 'PartyPrice':
          aValue = a.PartyPrice || 0;
          bValue = b.PartyPrice || 0;
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        case 'HotelPrice':
          aValue = a.HotelPrice || 0;
          bValue = b.HotelPrice || 0;
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        case 'WholeSalePrice':
          aValue = a.WholeSalePrice || 0;
          bValue = b.WholeSalePrice || 0;
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        case 'WEFDate':
          aValue = a.WEFDate ? new Date(a.WEFDate) : new Date(0);
          bValue = b.WEFDate ? new Date(b.WEFDate) : new Date(0);
          return newOrder === 'asc' ? aValue - bValue : bValue - aValue;
        
        default:
          return 0;
      }
    });
    
    setFilteredData(sortedData);
    // Reset to first page when sorting
    setPage(1);
  };

  // Function to handle segment change
  const handleBranchTypeChange = (event) => {
    setBranchType(event.target.value);
  };

  // Function to handle area change
  const handleAreaChange = (event) => {
    setArea(event.target.value);
  };

  // Function to handle branch change
  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
  };

  // Function to handle from date change
  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
  };

  // Function to handle to date change
  const handleToDateChange = (newValue) => {
    setToDate(newValue);
  };

  // Function to handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Function to handle search with debounce
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  // Improved function to filter data based on search query
  const filterData = () => {
    if (!searchQuery.trim()) {
      setFilteredData(reportData);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = reportData.filter(item => 
      (item.ItemName && item.ItemName.toLowerCase().includes(query)) ||
      (item.PartNumber && item.PartNumber.toLowerCase().includes(query)) ||
      (item.BranchName && item.BranchName.toLowerCase().includes(query)) ||
      (item.RateType && item.RateType.toLowerCase().includes(query))
    );
    
    setFilteredData(filtered);
    
    // Maintain sorting if already applied
    if (orderBy) {
      const isAsc = order === 'asc';
      const sortedData = [...filtered].sort((a, b) => {
        // Reuse sort logic
        let aValue, bValue;
        
        switch(orderBy) {
          case 'BranchName':
            aValue = a.BranchName || '';
            bValue = b.BranchName || '';
            return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          
          case 'PartNumber':
            aValue = a.PartNumber || '';
            bValue = b.PartNumber || '';
            return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          
          case 'ItemName':
            aValue = a.ItemName || '';
            bValue = b.ItemName || '';
            return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          
          case 'BusinessDate':
            aValue = a.BusinessDate ? new Date(a.BusinessDate) : new Date(0);
            bValue = b.BusinessDate ? new Date(b.BusinessDate) : new Date(0);
            return isAsc ? aValue - bValue : bValue - aValue;
          
          case 'RateType':
            aValue = a.RateType || '';
            bValue = b.RateType || '';
            return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          
          case 'RetailPrice':
            aValue = a.RetailPrice || 0;
            bValue = b.RetailPrice || 0;
            return isAsc ? aValue - bValue : bValue - aValue;
          
          case 'PartyPrice':
            aValue = a.PartyPrice || 0;
            bValue = b.PartyPrice || 0;
            return isAsc ? aValue - bValue : bValue - aValue;
          
          case 'HotelPrice':
            aValue = a.HotelPrice || 0;
            bValue = b.HotelPrice || 0;
            return isAsc ? aValue - bValue : bValue - aValue;
          
          case 'WholeSalePrice':
            aValue = a.WholeSalePrice || 0;
            bValue = b.WholeSalePrice || 0;
            return isAsc ? aValue - bValue : bValue - aValue;
          
          case 'WEFDate':
            aValue = a.WEFDate ? new Date(a.WEFDate) : new Date(0);
            bValue = b.WEFDate ? new Date(b.WEFDate) : new Date(0);
            return isAsc ? aValue - bValue : bValue - aValue;
          
          default:
            return 0;
        }
      });
      
      setFilteredData(sortedData);
    }
  };

  // Function to prepare data for CSV export with new response format
  const prepareCSVData = () => {
    const csvData = filteredData.map(item => ({
      Branch: item.BranchName || '',
      'Item Code': item.PartNumber || '',
      'Item Name': item.ItemName || '',
      'Business Date': item.BusinessDate ? dayjs(item.BusinessDate).format('DD/MM/YYYY') : '',
      // 'Rate Type': item.RateType || '',
      'Retail Price': item.RetailPrice || 0,
      'Party Price': item.PartyPrice || 0,
      'Hotel Price': item.HotelPrice || 0,
      'Wholesale Price': item.WholeSalePrice || 0,
      'WEF Date': item.WEFDate ? dayjs(item.WEFDate).format('DD/MM/YYYY') : ''
    }));
    
    return csvData;
  };

  // Function to export to CSV
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }
    
    // CSVLink component will handle the actual download
    document.getElementById('csvLinkClick').click();
  };

  // Create a sort handler for table headers
  const createSortHandler = (property) => () => {
    handleSort(property);
  };

  // Helper to get selected segment name
  const getSelectedSegmentName = () => {
    const seg = segments.find(s => s.businessSegmentCode === branchType);
    return seg ? seg.businessSegmentName : '';
  };

  // Helper to check if segment is Laziz
  const isLaziz = getSelectedSegmentName().toLowerCase() === 'laziz';
  // Helper to check if segment is Retail
  const isRetail = getSelectedSegmentName().toLowerCase() === 'retail';
  // Helper to check if ALL is selected for area and branch
  const isAllArea = area === 'ALL';
  const isAllBranch = selectedBranch && selectedBranch.branchID === 'ALL';
  // Helper to check if 3-day restriction applies
  const isRetailAllAll = isRetail && isAllArea && isAllBranch;

  // Compute if date range is valid
  const isDateRangeValid = (() => {
    if (!fromDate || !toDate) return false;
    const diff = toDate.diff(fromDate, 'day');
    if (toDate.isBefore(fromDate)) return false;
    if (isRetail) return diff <= 2;
    return diff <= 7;
  })();

  // Add ALL option to branch list
  const branchOptions = branchList && branchList.length > 0
    ? [{ branchID: 'ALL', branchName: 'ALL' }, ...branchList]
    : [{ branchID: 'ALL', branchName: 'ALL' }];

  return (
    <div className="report-page">
      <Header />
      <div className="customer-container">
        <div className="filter-container" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px', flexWrap: 'wrap' }}>
          {/* Branch Type (Segment) Dropdown - First dropdown */}
          <FormControl variant="outlined" style={{ minWidth: 130 }}>
            <InputLabel id="branch-type-select-label">Segment</InputLabel>
            <Select
              labelId="branch-type-select-label"
              value={branchType}
              onChange={handleBranchTypeChange}
              label="Segment"
            >
              {segments.map((segment) => (
                <MenuItem key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                  {segment.businessSegmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Area Dropdown - Second dropdown */}
          <FormControl variant="outlined" style={{ minWidth: 130, maxWidth: 130 }}>
            <InputLabel id="area-select-label">Area</InputLabel>
            <Select
              labelId="area-select-label"
              value={area}
              onChange={handleAreaChange}
              label="Area"
              disabled={!branchType}
            >
              <MenuItem value="ALL">All</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                  {area.SFAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Branch Autocomplete - Third dropdown */}
          {branchType && (
            <Autocomplete
              style={{ minWidth: 180, maxWidth: 180 }}
              options={branchOptions}
              getOptionLabel={(option) => option.branchName || ""}
              value={selectedBranch}
              onChange={handleBranchChange}
              inputValue={branchInputValue}
              onInputChange={(event, newInputValue) => {
                setBranchInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Branch"
                  variant="outlined"
                  size="medium"
                />
              )}
              isOptionEqualToValue={(option, value) => option.branchID === value?.branchID}
            />
          )}

          {/* Date Pickers */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={handleFromDateChange}
              // No minDate or maxDate for From Date
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 150, backgroundColor: '#F1F8E9' },
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={handleToDateChange}
              minDate={fromDate}
              maxDate={isRetail ? (fromDate.add(2, 'day').isAfter(dayjs()) ? dayjs() : fromDate.add(2, 'day')) : (fromDate.add(7, 'day').isAfter(dayjs()) ? dayjs() : fromDate.add(7, 'day'))}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 150, backgroundColor: '#F1F8E9' },
                  error: !isDateRangeValid,
                },
              }}
            />
          </LocalizationProvider>

          {/* Submit Button */}
          <Button
            onClick={fetchReportData}
            variant="contained"
            style={{ height: '40px' }}
            disabled={loading || !isDateRangeValid}
          >
            {loading ? 'Loading...' : 'Submit'}
          </Button>

          {/* Export Button */}
          <Button
            variant="contained"
            onClick={exportToCSV}
            style={{ height: '40px' }}
            disabled={reportData.length === 0}
          >
            Export
          </Button>
          
          {/* Hidden CSVLink component for export */}
          <CSVLink
            id="csvLinkClick"
            data={prepareCSVData()}
            filename={`BranchRateTrend_${dayjs().format('YYYYMMDD_HHmmss')}.csv`}
            className="hidden-csv-link"
            style={{ display: 'none' }}
          >
            Export to CSV
          </CSVLink>
        </div>

        {/* Error message */}
        {error && (
          <div style={{ color: 'red', padding: '10px' }}>
            {error}
          </div>
        )}

        {/* Search box */}
        {reportData.length > 0 && (
          <div style={{ padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              label="Search Item Name/Code/Branch"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearch}
              style={{ width: '180px' }}
            />

          </div>
        )}

        {/* Data table */}
        <div className="customer-table-container">
          <div className="customer-table-scroll">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>
                    <TableSortLabel
                      active={orderBy === 'BranchName'}
                      direction={orderBy === 'BranchName' ? order : 'asc'}
                      onClick={createSortHandler('BranchName')}
                    >
                      Branch
                    </TableSortLabel>
                  </th>
                  <th>
                    <TableSortLabel
                      active={orderBy === 'PartNumber'}
                      direction={orderBy === 'PartNumber' ? order : 'asc'}
                      onClick={createSortHandler('PartNumber')}
                    >
                      Item Code
                    </TableSortLabel>
                  </th>
                  <th>
                    <TableSortLabel
                      active={orderBy === 'ItemName'}
                      direction={orderBy === 'ItemName' ? order : 'asc'}
                      onClick={createSortHandler('ItemName')}
                    >
                      Item Name
                    </TableSortLabel>
                  </th>
                  <th>
                    <TableSortLabel
                      active={orderBy === 'BusinessDate'}
                      direction={orderBy === 'BusinessDate' ? order : 'asc'}
                      onClick={createSortHandler('BusinessDate')}
                    >
                      Business Date
                    </TableSortLabel>
                  </th>
                  {/* <th> */}
                    {/* <TableSortLabel
                      active={orderBy === 'RateType'}
                      direction={orderBy === 'RateType' ? order : 'asc'}
                      onClick={createSortHandler('RateType')}
                    >
                      Rate Type
                    </TableSortLabel> */}
                  {/* </th> */}
                  <th>
                    <TableSortLabel
                      active={orderBy === 'RetailPrice'}
                      direction={orderBy === 'RetailPrice' ? order : 'asc'}
                      onClick={createSortHandler('RetailPrice')}
                    >
                      Retail Price
                    </TableSortLabel>
                  </th>
                  {!isLaziz && (
                    <th>
                      <TableSortLabel
                        active={orderBy === 'PartyPrice'}
                        direction={orderBy === 'PartyPrice' ? order : 'asc'}
                        onClick={createSortHandler('PartyPrice')}
                      >
                        Party Price
                      </TableSortLabel>
                    </th>
                  )}
                  {!isLaziz && (
                    <th>
                      <TableSortLabel
                        active={orderBy === 'HotelPrice'}
                        direction={orderBy === 'HotelPrice' ? order : 'asc'}
                        onClick={createSortHandler('HotelPrice')}
                      >
                        Hotel Price
                      </TableSortLabel>
                    </th>
                  )}
                  {!isLaziz && (
                    <th>
                      <TableSortLabel
                        active={orderBy === 'WholeSalePrice'}
                        direction={orderBy === 'WholeSalePrice' ? order : 'asc'}
                        onClick={createSortHandler('WholeSalePrice')}
                      >
                        Wholesale Price
                      </TableSortLabel>
                    </th>
                  )}
                  <th>
                    <TableSortLabel
                      active={orderBy === 'WEFDate'}
                      direction={orderBy === 'WEFDate' ? order : 'asc'}
                      onClick={createSortHandler('WEFDate')}
                    >
                      WEF Date
                    </TableSortLabel>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center', padding: '50px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                        <span style={{ marginLeft: '10px' }}>Loading data...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={index}>
                      <td>{((page - 1) * ROWS_PER_PAGE) + index + 1}</td>
                      <td>{item.BranchName || '-'}</td>
                      <td>{item.PartNumber || '-'}</td>
                      <td>{item.ItemName || '-'}</td>
                      <td>{item.BusinessDate ? dayjs(item.BusinessDate).format('DD/MM/YYYY') : '-'}</td>
                      {/* <td>{item.RateType || '-'}</td> */}
                      <td>{item.RetailPrice ? item.RetailPrice.toFixed(2) : '0.00'}</td>
                      {!isLaziz && <td>{item.PartyPrice ? item.PartyPrice.toFixed(2) : '0.00'}</td>}
                      {!isLaziz && <td>{item.HotelPrice ? item.HotelPrice.toFixed(2) : '0.00'}</td>}
                      {!isLaziz && <td>{item.WholeSalePrice ? item.WholeSalePrice.toFixed(2) : '0.00'}</td>}
                      <td>{item.WEFDate ? dayjs(item.WEFDate).format('DD/MM/YYYY') : '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center' }}>
                      {reportData.length > 0 ? 'No matching records found' : 'No data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <Pagination 
              count={Math.ceil(filteredData.length / ROWS_PER_PAGE)} 
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default RateTrends;


